import useFilterCalendarDates from "app/hooks/calendars/useFilterCalendarDates";
import { useGetBumbalBeforeDays } from "app/hooks/checkout/useGetBumbalBeforeDays";
import { useCartContext } from "app/providers/CartProvider";
import { getMonth } from "date-fns";
import nl from "date-fns/locale/nl";
import { useCallback, useEffect, useMemo, useState } from "react";
import DatePicker, {
  ReactDatePickerCustomHeaderProps,
  registerLocale,
} from "react-datepicker";
import { STORAGE_ORDER_KEY } from "shared/config";
import Spinner from "shared/ui/spinner";
import Text from "shared/ui/typography/Text";
import { concatClassnames } from "shared/utils/concatClassnames";
import { getLocalDateString, months } from "shared/utils/date";

registerLocale("nl", nl);

interface CheckoutPanelDatePickerProps {
  className?: string;
}

// Initialize the current date
const currentDate = new Date();
// Get the current year from the current date
const currentYear = currentDate.getFullYear();
// Get the current month from the current date
const currentMonth = currentDate.getMonth();

// Function to get the first date of the current month
const monthStart = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth(), 1).getTime();

const CheckoutPanelDatePicker = ({
  className,
}: CheckoutPanelDatePickerProps) => {
  const order_id = localStorage.getItem(STORAGE_ORDER_KEY) ?? "";
  const { bumbalBeforeDays, isLoading, isError } = useGetBumbalBeforeDays(
    order_id,
    "pick-up",
  );

  const { filterPickUpEsdMethodDates, filterPickUpVillaArenaMethodDates } =
    useFilterCalendarDates();

  const isOngoingQuery = isLoading || isError;
  const {
    getCart,

    updateGeneralCartValue,
  } = useCartContext();
  const cart = getCart();

  const [firstAvailableDate, setFirstAvailableDate] = useState(currentDate);
  const [selectedDate, setSelectedDate] = useState(currentDate); // State to hold the selected date, initially set to the current date

  /* Using the useMemo hook to memoize the formattedDate. */
  const formattedDate = useMemo(() => {
    if (!cart.delivery.date) return "";
    return getLocalDateString(new Date(cart.delivery.date));
  }, [cart.delivery.date]);

  /* Using the useMemo hook to memoize the selectedDateText. */
  const selectedDateText = useMemo(() => {
    if (!cart.pickupWarehouse) return "Jouw bezorgafspraak";
    if (cart.pickupWarehouse === "ESD") return "Jouw afhaalafspraak";
    if (cart.pickupWarehouse === "VillaArena") return "Jouw afhaalafspraak";
  }, [cart.pickupWarehouse]);

  // Initially sync the first available date with the bumbalBeforeDays
  useEffect(() => {
    if (!bumbalBeforeDays || !bumbalBeforeDays.days) return;

    // Add delayed days
    const date = new Date(currentDate);
    date.setDate(date.getDate() + bumbalBeforeDays.days);

    // Ensure the date passes both filters
    while (
      !filterPickUpEsdMethodDates(date) ||
      !filterPickUpVillaArenaMethodDates(date)
    ) {
      date.setDate(date.getDate() + 1); // Increment the date by 1 day
    }

    // Set the first available date and selected date to the calculated date
    setFirstAvailableDate(date);
    setSelectedDate(date);

    updateGeneralCartValue({
      ...cart,
      delivery: {
        ...cart.delivery,
        date: date,
      },
    });
  }, [bumbalBeforeDays]);

  // This function filters the date based on the selected pickup warehouse
  const filterDate = useCallback(
    (date: Date) => {
      if (cart.pickupWarehouse === "ESD") {
        // If the pickup warehouse is ESD, filter based on ESD pickup method dates
        return filterPickUpEsdMethodDates(date);
      } else if (cart.pickupWarehouse === "VillaArena") {
        // If the pickup warehouse is VillaArena, filter based on VillaArena pickup method dates
        return filterPickUpVillaArenaMethodDates(date);
      } else {
        // If the pickup warehouse is not recognized, return false
        return false;
      }
    },
    [cart.pickupWarehouse],
  );

  /**
   * If the day does not belong to the selected month, return an empty div, otherwise, render the day number
   * @param {number} day - The day number of the month.
   * @param {Date} date - The date of the day being rendered
   * @returns A function that takes in a day and a date and returns a div.
   */
  const renderDayContents = (day: number, date: Date) => {
    // If the day does not belong to the selected month, return an empty div
    if (date.getMonth() !== selectedDate?.getMonth()) {
      return <div></div>;
    }
    // Otherwise, render the day number
    return <div>{day}</div>;
  };

  /**
   * It returns an array of months that are available for booking
   * @returns An array of strings.
   */
  const availableDropDownMonths = (count = 5) => {
    const availableMonths: string[] = [];

    for (let i = 0; i < count; i++) {
      const date = new Date(
        firstAvailableDate.getFullYear(),
        firstAvailableDate.getMonth() + i,
        1,
      );

      availableMonths.push(months[date.getMonth()]);
    }

    return availableMonths;
  };

  const onMonthChange = (date: Date) => {
    if (monthStart(date) > monthStart(firstAvailableDate)) {
      setSelectedDate(date);
    } else {
      setSelectedDate(firstAvailableDate);
    }
  };

  const customHeaderElement = ({
    date,
    decreaseMonth,
    increaseMonth,
    changeMonth,
  }: ReactDatePickerCustomHeaderProps) => {
    const availableMonths = availableDropDownMonths();
    const disablePrevMonth = months[getMonth(date)] === availableMonths[0];
    const disableFirstMonth =
      months[getMonth(date)] === months[getMonth(firstAvailableDate)];
    const disableLastMonth =
      months[getMonth(date)] ===
      availableDropDownMonths()[availableMonths.length - 1];
    return (
      <>
        <div
          className={
            "gg-mb-8 gg-grid gg-w-full gg-grid-cols-3 gg-text-left gg-font-bold"
          }
        >
          <div className="gg-flex gg-w-full gg-items-center">
            {!disablePrevMonth && !disableFirstMonth && (
              <button
                className="gg-transition gg-duration-300 gg-ease-in-out hover:gg-scale-125 hover:gg-text-dark-blue"
                onClick={decreaseMonth}
                disabled={disablePrevMonth}
              >
                {"<"}
              </button>
            )}
          </div>

          <div className="gg-mr-8 gg-flex gg-w-full gg-items-center gg-justify-center">
            <select
              className="gg-peer gg-flex gg-h-full gg-w-36 gg-cursor-pointer gg-rounded-lg gg-border-r-8 gg-border-transparent gg-px-4 gg-py-3 gg-text-sm gg-text-dark-blue gg-outline gg-outline-2 gg-outline-gray-400 gg-transition-all focus:gg-outline-2 focus:gg-outline-dark-blue focus:gg-ring-dark-blue"
              value={months[getMonth(date)]}
              onChange={({ target: { value } }) =>
                changeMonth(months.indexOf(value))
              }
            >
              {availableMonths.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
          <div className="gg-flex gg-w-full gg-items-center gg-justify-end">
            {!disableLastMonth && (
              <button
                className="gg-whitespace-nowrap gg-rounded-sm gg-text-slate-800 gg-transition gg-duration-300 gg-ease-in-out hover:gg-scale-125"
                onClick={increaseMonth}
                disabled={disableLastMonth}
              >
                {">"}
              </button>
            )}
          </div>
        </div>
      </>
    );
  };
  return (
    <div className={concatClassnames("", className)}>
      <div className="gg-grid gg-w-full gg-grid-cols-12 gg-divide-y gg-divide-dark-blue gg-bg-grey-500 md:gg-divide-x md:gg-divide-y-0">
        <div className="gg-relative gg-col-span-12 gg-px-1 gg-py-5 sm:gg-px-8 sm:gg-py-8">
          {isOngoingQuery ? (
            <DatePicker
              selected={null} // Set selected date to the pageDate state
              onChange={(date) => {}}
              inline
              locale={"nl"}
              readOnly={true}
              disabled={true}
              filterDate={() => {
                return false;
              }}
              renderCustomHeader={customHeaderElement}
            >
              {isLoading && (
                <div className="gg-z-2 gg-absolute gg-inset-0 gg-grid gg-h-96 gg-w-full gg-place-items-center gg-content-center gg-items-center gg-gap-x-4 gg-gap-y-5 gg-px-5 gg-backdrop-blur-lg">
                  <Spinner className="gg-shrink-0 gg-text-black" />
                  <h3 className="gg-text-base gg-font-semibold gg-text-dark-blue">
                    Momentje, de beschikbare mogelijkheden worden voor je
                    opgehaald.
                  </h3>
                </div>
              )}

              {isError && (
                <div className="gg-z-2 gg-absolute gg-inset-0 gg-grid gg-h-96 gg-w-full gg-place-items-center gg-content-center gg-items-center gg-gap-x-4 gg-px-5 gg-backdrop-blur-lg">
                  <h3>
                    Er is iets misgegaan met uw betaling. Neem voor meer
                    informatie contact op met klantenservice.
                  </h3>
                </div>
              )}
            </DatePicker>
          ) : (
            <DatePicker
              inline
              className={"gg-react-datepicker"}
              selected={selectedDate}
              onChange={(date: Date) => {
                setSelectedDate(date);

                updateGeneralCartValue({
                  ...cart,
                  delivery: {
                    ...cart.delivery,
                    date: date,
                  },
                });
              }}
              open={true}
              minDate={firstAvailableDate}
              filterDate={filterDate}
              nextMonthButtonLabel=">"
              previousMonthButtonLabel="<"
              locale={"nl"}
              disabledKeyboardNavigation
              renderDayContents={renderDayContents}
              onMonthChange={(date) => onMonthChange(date)}
              renderCustomHeader={customHeaderElement}
            />
          )}
        </div>
      </div>

      <div className="gg-bg-dark-blue-200 gg-px-6 gg-py-5">
        <Text className={"gg-grid sm:gg-flex"}>
          <span className="gg-mr-4 gg-font-bold">
            {selectedDateText}
            <span className="sm:gg-hidden">:</span>
          </span>
          <span>
            {formattedDate ? <>{formattedDate}</> : <>{"Geen datum gekozen"}</>}
          </span>
        </Text>
      </div>
    </div>
  );
};
export default CheckoutPanelDatePicker;
